import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Clock } from "lucide-react";

type BusinessHour = {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_enabled: boolean;
};

const DAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

export const BusinessHours = () => {
  const [hours, setHours] = useState<BusinessHour[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBusinessHours();
  }, []);

  const fetchBusinessHours = async () => {
    const { data, error } = await supabase
      .from('business_hours')
      .select('*')
      .order('day_of_week');

    if (error) {
      console.error('Error fetching business hours:', error);
      toast.error("Failed to load business hours");
      setLoading(false);
      return;
    }

    // Initialize all days if they don't exist
    const existingDays = data?.map(h => h.day_of_week) || [];
    const missingDays = DAYS.filter(d => !existingDays.includes(d.value));
    
    const defaultHours = missingDays.map(day => ({
      id: '',
      day_of_week: day.value,
      start_time: '09:00',
      end_time: '17:00',
      is_enabled: false,
    }));

    setHours([...(data || []), ...defaultHours].sort((a, b) => a.day_of_week - b.day_of_week));
    setLoading(false);
  };

  const handleToggleDay = async (dayIndex: number) => {
    const hour = hours[dayIndex];
    const newIsEnabled = !hour.is_enabled;

    const updatedHours = [...hours];
    updatedHours[dayIndex] = { ...hour, is_enabled: newIsEnabled };
    setHours(updatedHours);

    await saveBusinessHour(updatedHours[dayIndex]);
  };

  const handleTimeChange = async (dayIndex: number, field: 'start_time' | 'end_time', value: string) => {
    const updatedHours = [...hours];
    updatedHours[dayIndex] = { ...updatedHours[dayIndex], [field]: value };
    setHours(updatedHours);

    await saveBusinessHour(updatedHours[dayIndex]);
  };

  const saveBusinessHour = async (hour: BusinessHour) => {
    if (hour.id) {
      // Update existing record
      const { error } = await supabase
        .from('business_hours')
        .update({
          start_time: hour.start_time,
          end_time: hour.end_time,
          is_enabled: hour.is_enabled,
        })
        .eq('id', hour.id);

      if (error) {
        console.error('Error updating business hour:', error);
        toast.error("Failed to update business hours");
      } else {
        toast.success("Business hours updated");
      }
    } else {
      // Create new record
      const { data, error } = await supabase
        .from('business_hours')
        .insert({
          day_of_week: hour.day_of_week,
          start_time: hour.start_time,
          end_time: hour.end_time,
          is_enabled: hour.is_enabled,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating business hour:', error);
        toast.error("Failed to create business hours");
      } else {
        // Update the ID in state
        const updatedHours = hours.map(h => 
          h.day_of_week === hour.day_of_week ? { ...h, id: data.id } : h
        );
        setHours(updatedHours);
        toast.success("Business hours created");
      }
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Loading business hours...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Business Hours
        </CardTitle>
        <CardDescription>
          Configure your operating hours. Used by "After Hours" forwarding mode to determine when to forward messages.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {hours.map((hour, index) => {
          const day = DAYS.find(d => d.value === hour.day_of_week);
          return (
            <div key={hour.day_of_week} className="flex items-center gap-4 p-4 border rounded-lg">
              <div className="flex-1">
                <Label className="font-medium">{day?.label}</Label>
              </div>
              
              <Switch
                checked={hour.is_enabled}
                onCheckedChange={() => handleToggleDay(index)}
              />

              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={hour.start_time}
                  onChange={(e) => handleTimeChange(index, 'start_time', e.target.value)}
                  disabled={!hour.is_enabled}
                  className="w-32"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  type="time"
                  value={hour.end_time}
                  onChange={(e) => handleTimeChange(index, 'end_time', e.target.value)}
                  disabled={!hour.is_enabled}
                  className="w-32"
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
